import { useContext, useEffect, useState } from "react";
import { PspContext } from "../../../../config/contexts";
import { KeyLabelPair } from "@/config/types";
import { translate } from "@/config/translator";
import {
  downloadFile as downloadFileApi,
  downloadPspDocument,
  Folder,
  getMandateStructure,
  getPspDocumentsPage,
  PspDocument,
  File as PspFile,
} from "@/api/openapi";
import { toast } from "react-toastify";
import {
  downloadBlob,
  downloadFile,
  getFileFromBlobResponse,
  getFileOrFolderFromPath,
} from "@/config/utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { decodePathFromUrl } from "../../submittedDocuments/submittedDocumentsUtils";
import { useIsTablet } from "@/config/hooks";

type DocumentKind = "submitted" | "psp";
const LIMIT = 50;

export function useDocumentsMobile() {
  const { lang, selectedMandateId } = useContext(PspContext);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const documentKind: DocumentKind = pathname.split("/")[2] as DocumentKind;
  const { "*": path } = useParams();
  const decodedPath = decodePathFromUrl(path);

  const [pspDocuments, setPspDocuments] = useState<PspDocument[]>();
  const [page, setPage] = useState(0);
  const [searchQuery, useSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState<number>();
  const [structure, setStructure] = useState<Folder>();
  const [currentStructure, setCurrentStructure] = useState<Folder | PspFile>();
  const [currentFilePreview, setCurrentFilePreview] = useState<File | null>(
    null,
  );

  useEffect(() => {
    if (selectedMandateId) {
      getMandateStructure({ path: { mandate_id: selectedMandateId } }).then(
        ({ data }) => {
          if (!data) throw new Error("Could not get mandate structure.");
          setStructure(data);
        },
        () => {
          toast(translate("couldntLoadMandateStructure", lang));
        },
      );
    }
  }, [lang, selectedMandateId]);

  const isTablet = useIsTablet();
  useEffect(() => {
    if (isTablet) {
      navigate("/psp-documents");
    }
  }, [isTablet, navigate]);

  function onOpenPreview(documentId: string) {
    downloadPspDocument({
      path: { document_id: documentId },
      responseType: "blob",
    }).then((response) => {
      if (response.error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setCurrentFilePreview(getFileFromBlobResponse(response));
      }
    });
  }

  useEffect(() => {
    if (structure) {
      setCurrentStructure(
        getFileOrFolderFromPath(structure, decodedPath ?? structure.path),
      );
    }
  }, [currentStructure?.path, decodedPath, structure]);

  const documentKindOptions: KeyLabelPair<DocumentKind>[] = [
    {
      key: "submitted",
      label: translate("submitted", lang),
    },
    {
      key: "psp",
      label: translate("psp", lang),
    },
  ];

  const actualSearchQuery = searchQuery.length >= 2 ? searchQuery : undefined;

  useEffect(() => {
    if (selectedMandateId) {
      getPspDocumentsPage({
        query: {
          mandate_id: selectedMandateId,
        },
        body: {
          limit: LIMIT,
          offset: LIMIT * page,
          query: actualSearchQuery,
        },
      }).then(({ data, error }) => {
        if (error) {
          toast(translate("couldntLoadPspDocuments", lang));
        } else {
          setPspDocuments(data.data);
          setTotalPages(Math.ceil(data.total / LIMIT) || 1);
        }
      });
    }
  }, [page, selectedMandateId, actualSearchQuery, searchQuery, lang]);

  function onDownload(filePath?: string) {
    if (typeof filePath === "string") {
      downloadFileApi({ query: { path: filePath }, responseType: "blob" }).then(
        (response) => {
          if (response.error) {
            toast(translate("somethingWentWrong", lang));
          } else {
            downloadBlob(response);
          }
        },
      );
    } else if (currentFilePreview) {
      downloadFile(currentFilePreview);
      setCurrentFilePreview(null);
    } else {
      toast(translate("somethingWentWrong", lang));
    }
  }

  return {
    lang,
    documentKind,
    documentKindOptions,
    totalPages,
    page,
    setPage,
    setTotalPages,
    pspDocuments,
    useSearchQuery,
    navigate,
    currentStructure,
    decodedPath,
    onDownload,
    currentFilePreview,
    setCurrentFilePreview,
    onOpenPreview,
  };
}
